/**
 * ConversationContextPanel — the context rail beside an open conversation (NextAdmin demo `/chats`: the right column
 * with who is in the chat and what it is about). Cards: summary, what the conversation is about, intent, artifacts,
 * attached context, members, the members' threads, the context the agent was given.
 */
import { useQuery } from '@tanstack/react-query';
import { Route } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Badge } from '../../../components/ui/Badge.js';
import { Callout } from '../../../components/ui/Callout.js';
import { Card } from '../../../components/ui/Card.js';
import { sentenceCase } from '../../../components/ui/FilterSelect.js';
import { SkeletonList } from '../../../components/ui/Skeleton.js';
import { ConversationAttachmentsList } from '../../../components/conversations/ConversationAttachmentsList.js';
import { ConversationRoster } from '../../../components/conversations/ConversationRoster.js';
import { SubagentChildrenList } from '../../../components/conversations/SubagentChildrenList.js';
import { ConversationEntitiesList } from '../../../components/conversations/entities/ConversationEntitiesList.js';
import { workingMemberIds } from '../../../components/conversations/conversation-members-model.js';
import {
  getConversationContextOptions,
  getConversationOptions,
  getTaskOptions,
} from '../../../generated/api/@tanstack/react-query.gen.js';
import { listConversationIntentsOptions } from '../../../generated/api/@tanstack/react-query.gen.js';
import type { Conversation } from '../../../generated/api/types.gen.js';
import { useConversationMembers } from '../../../hooks/useConversationMembers.js';
import type { StreamMessage } from '../../../hooks/useConversationStream.js';
import { useProject } from '../../../hooks/useProjects.js';
import { relativeTime } from '../../../utils/relative-time.js';
import { ConversationArtifactsSection } from './ConversationArtifactsSection.js';
import { KIND_LABEL, STATUS_LABEL, STATUS_VARIANT, subjectLabel } from '../conversations-list/conversation-labels.js';

interface ConversationContextPanelProps {
  conv: Conversation;
  conversationId: string;
  /** The newest message's time: a summary older than it is stale. */
  newestMessageAt: string | null;
  /** The conversation's messages (the page's one stream): who is working, and members to fetch again. */
  messages: readonly StreamMessage[];
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2 text-sm">
      <dt className="shrink-0 text-muted">{label}</dt>
      <dd className="min-w-0 text-right text-heading">{children}</dd>
    </div>
  );
}

function SummaryCard({ conv, newestMessageAt }: { conv: Conversation; newestMessageAt: string | null }) {
  const summary = conv.structuredSummary;
  const stale =
    conv.summaryUpdatedAt !== null &&
    newestMessageAt !== null &&
    new Date(conv.summaryUpdatedAt).getTime() < new Date(newestMessageAt).getTime();
  const points = summary ? [...summary.goals, ...summary.decisions.map((d) => d.text)] : [];
  return (
    <Card title="Summary">
      {stale && conv.summaryUpdatedAt && (
        <Callout tone="warning" className="mb-4">
          Updated {relativeTime(conv.summaryUpdatedAt)}. Newer messages are not in it yet.
        </Callout>
      )}
      {points.length > 0 ? (
        <ul className="list-disc space-y-1.5 pl-5 text-sm text-body">
          {points.map((point) => (
            <li key={point}>{point}</li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted">No summary yet. It appears after the first exchange.</p>
      )}
      {summary && summary.openQuestions.length > 0 && (
        <div className="mt-4">
          <p className="mb-1.5 text-xs font-medium text-muted">Open questions</p>
          <ul className="list-disc space-y-1.5 pl-5 text-sm text-body">
            {summary.openQuestions.map((question) => (
              <li key={question}>{question}</li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}

function AboutCard({ conv }: { conv: Conversation }) {
  const project = useProject(conv.projectId ?? undefined);
  const parent = useQuery({
    ...getConversationOptions({ path: { id: String(conv.parentConversationId ?? '') } }),
    enabled: conv.parentConversationId !== null,
  });
  const task = useQuery({
    ...getTaskOptions({ path: { id: String(conv.rootTaskId ?? '') } }),
    enabled: conv.rootTaskId !== null,
  });
  return (
    <Card title="About">
      <dl className="divide-y">
        <Row label="Type">{KIND_LABEL[conv.kind]}</Row>
        <Row label="Status">
          <Badge variant={STATUS_VARIANT[conv.status]} dot>
            {STATUS_LABEL[conv.status]}
          </Badge>
        </Row>
        {conv.subjectType && <Row label="About">{subjectLabel(conv.subjectType)}</Row>}
        {conv.projectId !== null && (
          <Row label="Project">
            <Link to={`/projects/${conv.projectId}`} className="text-accent-text hover:underline">
              {project.data?.name ?? 'Open project'}
            </Link>
          </Row>
        )}
        {conv.rootTaskId !== null && (
          <Row label="Task">
            <Link to={`/tasks/${conv.rootTaskId}`} className="text-accent-text hover:underline">
              {task.data?.title ?? 'Open task'}
            </Link>
          </Row>
        )}
        {conv.parentConversationId !== null && (
          <Row label="Started from">
            <Link to={`/conversations/${conv.parentConversationId}`} className="text-accent-text hover:underline">
              {parent.data?.topic ?? 'Open conversation'}
            </Link>
          </Row>
        )}
      </dl>
    </Card>
  );
}

function IntentCard({ conversationId }: { conversationId: string }) {
  const { data, isError } = useQuery(
    listConversationIntentsOptions({ path: { id: conversationId }, query: { limit: 25 } }),
  );
  const latest = data?.data[0] ?? null;
  return (
    <Card title="How it was routed">
      {isError ? (
        <p className="text-sm text-danger">Could not load the routing.</p>
      ) : latest ? (
        <div className="space-y-2">
          <Badge variant="purple" icon={<Route size={12} aria-hidden />}>
            {sentenceCase(latest.classification.kind)} · {Math.round(latest.classification.confidence * 100)}% sure
          </Badge>
          <p className="text-sm text-muted">{latest.classification.rationale}</p>
        </div>
      ) : (
        <p className="text-sm text-muted">Nothing routed yet.</p>
      )}
    </Card>
  );
}

/** Who is in the conversation, the lead, and who is working right now (conversation-members spec). */
function MembersCard({ conversationId, messages }: { conversationId: string; messages: readonly StreamMessage[] }) {
  const { members, me, threads, isLoading, isError } = useConversationMembers(conversationId, messages);
  const navigate = useNavigate();
  return (
    <Card title="Members">
      {isLoading ? (
        <SkeletonList count={3} label="Loading members" />
      ) : isError ? (
        <Callout tone="danger">Could not load the members.</Callout>
      ) : (
        <ConversationRoster
          conversationId={conversationId}
          members={members}
          me={me}
          working={workingMemberIds(messages)}
          threads={threads}
          // A member's row opens its thread at its own route, in place of this conversation.
          onOpenConversation={(threadId) => navigate(`/conversations/${threadId}`)}
        />
      )}
    </Card>
  );
}

function ContextPackCard({ conversationId }: { conversationId: string }) {
  const { data } = useQuery(getConversationContextOptions({ path: { id: conversationId } }));
  const pack = data?.lastContextPackPublicView ?? null;
  return (
    <Card title="What the agent was given">
      {pack ? (
        <p className="text-sm text-body">
          {pack.template.name} · {pack.skills.length} {pack.skills.length === 1 ? 'skill' : 'skills'} ·{' '}
          {pack.tools.length} {pack.tools.length === 1 ? 'tool' : 'tools'}
          <span className="block text-xs text-muted">Assembled {relativeTime(pack.assembledAt)}</span>
        </p>
      ) : (
        <p className="text-sm text-muted">Nothing assembled yet. It appears with the first reply.</p>
      )}
    </Card>
  );
}

export function ConversationContextPanel({
  conv,
  conversationId,
  newestMessageAt,
  messages,
}: ConversationContextPanelProps) {
  return (
    <div className="space-y-4">
      <SummaryCard conv={conv} newestMessageAt={newestMessageAt} />
      <AboutCard conv={conv} />
      <IntentCard conversationId={conversationId} />
      <Card title="Artifacts">
        <ConversationArtifactsSection conversationId={conversationId} />
      </Card>
      {/* What the conversation was given to work from (memories, tasks, other conversations), attached and detached here. */}
      <Card>
        <ConversationAttachmentsList conversationId={conversationId} />
      </Card>
      <MembersCard conversationId={conversationId} messages={messages} />
      {/* What the conversation works on, each opening in the side panel over this page (owner 2026-10-31). */}
      <Card title="In this conversation">
        <ConversationEntitiesList conversationId={conversationId} newestMessageId={messages.at(-1)?.id ?? null} />
      </Card>
      {/* The threads members worked on for this conversation; each answer links its own. */}
      <SubagentChildrenList parentConversationId={conversationId} />
      <ContextPackCard conversationId={conversationId} />
    </div>
  );
}
